import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLacOverallCostsCard } from '../../features/lac/hooks/useLacOverallCostsCard';
import { useI18n } from '../../i18n/useI18n';
import { formatCost } from '../../utils/formatUtils';
import './LacOverallCostsCard.css';

export function LacOverallCostsCard() {
  const { locale, translate } = useI18n();
  const {
    isExpanded,
    toggleExpanded,
    year,
    selectedMonth,
    monthCells,
    monthlyTotal,
    yearlyTotal,
    canGoNextYear,
    canGoNextMonth,
    goPrevYear,
    goNextYear,
    goPrevMonth,
    goNextMonth,
    selectMonth,
    isLoading,
    isError,
  } = useLacOverallCostsCard();

  const panelId = 'lac-overall-costs-panel';
  const selectedMonthLabel = new Date(
    year,
    selectedMonth - 1,
    1,
  ).toLocaleDateString(locale, { month: 'long', year: 'numeric' });

  return (
    <section className="lac-overall-costs">
      <button
        type="button"
        className="lac-overall-costs__toggle"
        aria-expanded={isExpanded}
        aria-controls={panelId}
        aria-label={
          isExpanded
            ? translate('lac.organisations.overallCosts.collapse')
            : translate('lac.organisations.overallCosts.expand')
        }
        onClick={toggleExpanded}
      >
        <span className="lac-overall-costs__title">
          {translate('lac.organisations.overallCosts.title')}
        </span>
        <ChevronDown
          className={[
            'lac-overall-costs__chevron',
            isExpanded && 'lac-overall-costs__chevron--expanded',
          ]
            .filter(Boolean)
            .join(' ')}
          strokeWidth={2}
          aria-hidden
        />
      </button>

      {isExpanded ? (
        <div id={panelId} className="lac-overall-costs__body">
          <div className="lac-overall-costs__toolbar">
            <nav
              className="lac-overall-costs__nav"
              aria-label={translate(
                'lac.organisations.overallCosts.monthNavLabel',
              )}
            >
              <button
                type="button"
                className="lac-overall-costs__nav-btn"
                onClick={goPrevMonth}
                aria-label={translate('common.dateRange.prevMonth')}
              >
                <ChevronLeft size={16} aria-hidden />
              </button>
              <span className="lac-overall-costs__nav-label">
                {selectedMonthLabel}
              </span>
              <button
                type="button"
                className="lac-overall-costs__nav-btn"
                onClick={goNextMonth}
                disabled={!canGoNextMonth}
                aria-label={translate('common.dateRange.nextMonth')}
              >
                <ChevronRight size={16} aria-hidden />
              </button>
            </nav>

            <nav
              className="lac-overall-costs__nav"
              aria-label={translate(
                'lac.organisations.overallCosts.yearNavLabel',
              )}
            >
              <button
                type="button"
                className="lac-overall-costs__nav-btn"
                onClick={goPrevYear}
                aria-label={translate(
                  'lac.organisations.overallCosts.previousYear',
                )}
              >
                <ChevronLeft size={16} aria-hidden />
              </button>
              <span className="lac-overall-costs__nav-label lac-overall-costs__nav-label--year">
                {year}
              </span>
              <button
                type="button"
                className="lac-overall-costs__nav-btn"
                onClick={goNextYear}
                disabled={!canGoNextYear}
                aria-label={translate(
                  'lac.organisations.overallCosts.nextYear',
                )}
              >
                <ChevronRight size={16} aria-hidden />
              </button>
            </nav>
          </div>

          {isLoading ? (
            <p className="lac-overall-costs__status">
              {translate('lac.organisations.overallCosts.loading')}
            </p>
          ) : isError ? (
            <p className="lac-overall-costs__status" role="alert">
              {translate('lac.organisations.overallCosts.loadError')}
            </p>
          ) : (
            <>
              <div className="lac-overall-costs__totals">
                <div className="lac-overall-costs__metric">
                  <span className="lac-overall-costs__metric-label">
                    {translate('lac.organisations.overallCosts.monthlyTotal')}
                  </span>
                  <span className="lac-overall-costs__metric-value">
                    {formatCost(monthlyTotal, locale)}
                  </span>
                  <span className="lac-overall-costs__metric-hint">
                    {selectedMonthLabel}
                  </span>
                </div>
                <div className="lac-overall-costs__metric">
                  <span className="lac-overall-costs__metric-label">
                    {translate('lac.organisations.overallCosts.yearlyTotal')}
                  </span>
                  <span className="lac-overall-costs__metric-value">
                    {formatCost(yearlyTotal, locale)}
                  </span>
                  <span className="lac-overall-costs__metric-hint">
                    {translate('lac.organisations.overallCosts.yearHint', {
                      year: String(year),
                    })}
                  </span>
                </div>
              </div>

              <div
                className="lac-overall-costs__months"
                role="listbox"
                aria-label={translate(
                  'lac.organisations.overallCosts.monthsLegend',
                )}
              >
                {monthCells.map((cell) => {
                  const monthName = new Date(
                    year,
                    cell.month - 1,
                    1,
                  ).toLocaleDateString(locale, { month: 'short' });
                  const costLabel = cell.isFuture
                    ? '—'
                    : formatCost(cell.cost, locale);

                  return (
                    <button
                      key={cell.key}
                      type="button"
                      role="option"
                      aria-selected={cell.isSelected}
                      className={[
                        'lac-overall-costs__month',
                        cell.isSelected && 'lac-overall-costs__month--selected',
                        cell.isFuture && 'lac-overall-costs__month--future',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      disabled={cell.isFuture}
                      onClick={() => selectMonth(cell.month)}
                      aria-label={
                        cell.isFuture
                          ? translate(
                              'lac.organisations.overallCosts.monthUnavailable',
                              { month: monthName },
                            )
                          : translate(
                              'lac.organisations.overallCosts.monthOption',
                              {
                                month: monthName,
                                cost: costLabel,
                              },
                            )
                      }
                    >
                      <span className="lac-overall-costs__month-name">
                        {monthName}
                      </span>
                      <span className="lac-overall-costs__month-cost">
                        {costLabel}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}
